import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { createOrUpdateUser } from '../services/userService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Tracks whether the first `users/{uid}` snapshot has fired (or that we're
  // logged out, in which case admin status is trivially "loaded as false").
  // Routes that gate on isAdmin must wait for this to flip true before
  // deciding whether to redirect; otherwise cold-load of an admin URL races
  // the Firestore listener and bounces the user to /all-projects.
  const [adminLoaded, setAdminLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshAdminStatus = async () => {
    if (currentUser) {
      try {
        // Get current user document from Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const isAdminInFirestore = !!userData.isAdmin;
          
          // Update local state
          setIsAdmin(isAdminInFirestore);
        } else {
          setIsAdmin(false);
        }
        setAdminLoaded(true);
      } catch (error) {
        setIsAdmin(false);
        setAdminLoaded(true);
      }
    } else {
      setIsAdmin(false);
      setAdminLoaded(true);
    }
  };

  useEffect(() => {
    let userDocUnsubscribe = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsAdmin(false); // Reset on logout or user change
      setAdminLoaded(false); // Reset; will flip true on first snapshot below.

      // Clean up previous listener
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }

      if (user) {
        try {
          // Create or update user document in the new users collection
          await createOrUpdateUser(user.uid, user.email, user.displayName || user.email);

          // Set up Firestore snapshot listener for user document
          const userDocRef = doc(db, 'users', user.uid);
          userDocUnsubscribe = onSnapshot(userDocRef, { includeMetadataChanges: true }, async (docSnap) => {
            const isFromCache = docSnap.metadata.fromCache;

            if (docSnap.exists()) {
              const userData = docSnap.data();

              // Check if user is pending and set as active if so
              if (userData.pendingUser) {
                try {
                  const { httpsCallable } = await import('firebase/functions');
                  const { functions } = await import('../firebase');
                  const setUserAsActive = httpsCallable(functions, 'setUserAsActive');
                  await setUserAsActive();
                } catch (error) {
                  // Silent fail for user activation
                }
              }

              // Update admin status based on Firestore
              const isAdminInFirestore = !!userData.isAdmin;
              setIsAdmin(isAdminInFirestore);

              // A cached non-admin snapshot can be stale for admin users on a
              // cold route load. Wait for the server snapshot before allowing
              // admin-gated routes to redirect.
              if (isAdminInFirestore || !isFromCache) {
                setAdminLoaded(true);
              }
            } else {
              // User document doesn't exist, set as non-admin
              setIsAdmin(false);
              if (!isFromCache) {
                setAdminLoaded(true);
              }
            }
          }, (error) => {
            setIsAdmin(false);
            setAdminLoaded(true); // Listener errored — proceed with non-admin.
          });

        } catch (err) {
          setIsAdmin(false);
          setAdminLoaded(true); // createOrUpdateUser threw — proceed with non-admin.
        }
      } else {
        // Logged out — admin status is trivially "loaded as false".
        setAdminLoaded(true);
      }
      setLoading(false);
    });
    
    return () => {
      unsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
    };
  }, []);

  const value = {
    currentUser,
    isAdmin,
    adminLoaded,
    refreshAdminStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
